import { useCallback, useState } from 'react';
import {
  AssetShareComment,
  AssetShareImageInfo,
  AssetShareLinkInfo,
  getShareLinkComments,
  listAssetShareLinks,
} from '../../../api/assetShare';

export function useAssetShareHistory(projectId: string | undefined, messages: {
  loadHistoryFailed: string;
  loadCommentsFailed: string;
}) {
  const [assetShareHistory, setAssetShareHistory] = useState<AssetShareLinkInfo[]>([]);
  const [showShareHistory, setShowShareHistory] = useState(false);
  const [shareHistoryLoading, setShareHistoryLoading] = useState(false);
  const [selectedShareLink, setSelectedShareLink] = useState<AssetShareLinkInfo | null>(null);
  const [selectedShareComments, setSelectedShareComments] = useState<AssetShareComment[]>([]);
  const [shareCommentsLoading, setShareCommentsLoading] = useState(false);
  const [shareHistoryError, setShareHistoryError] = useState('');
  const [selectedShareImageMap, setSelectedShareImageMap] = useState<Record<string, AssetShareImageInfo>>({});
  const [shareCommentsError, setShareCommentsError] = useState('');

  const loadShareHistory = useCallback(async () => {
    if (!projectId) return;
    setShareHistoryLoading(true);
    setShareHistoryError('');
    try {
      const links = await listAssetShareLinks(projectId);
      setAssetShareHistory(links);
    } catch {
      setShareHistoryError(messages.loadHistoryFailed);
    } finally {
      setShareHistoryLoading(false);
    }
  }, [projectId, messages.loadHistoryFailed]);

  const openShareHistory = useCallback(() => {
    setShowShareHistory(true);
    setShareHistoryError('');
    loadShareHistory();
  }, [loadShareHistory]);

  const closeShareHistory = useCallback(() => {
    setShowShareHistory(false);
    setSelectedShareLink(null);
  }, []);

  const loadShareComments = useCallback(async (link: AssetShareLinkInfo) => {
    if (!projectId) return;
    setSelectedShareLink(link);
    setShareCommentsLoading(true);
    setShareCommentsError('');
    try {
      const { comments, imageMap } = await getShareLinkComments(projectId, link.id);
      setSelectedShareComments(comments);
      setSelectedShareImageMap(imageMap);
    } catch {
      setSelectedShareComments([]);
      setSelectedShareImageMap({});
      setShareCommentsError(messages.loadCommentsFailed);
    } finally {
      setShareCommentsLoading(false);
    }
  }, [projectId, messages.loadCommentsFailed]);

  const backToShareHistory = useCallback(() => {
    setSelectedShareLink(null);
    setSelectedShareComments([]);
    setSelectedShareImageMap({});
  }, []);

  return {
    assetShareHistory,
    backToShareHistory,
    closeShareHistory,
    loadShareComments,
    openShareHistory,
    selectedShareComments,
    selectedShareImageMap,
    selectedShareLink,
    shareCommentsError,
    shareCommentsLoading,
    shareHistoryError,
    shareHistoryLoading,
    showShareHistory,
  };
}
